import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ClockCounterClockwise, MagnifyingGlass, Trash, ArrowClockwise, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_META = {
  login_success:  { label: 'Login',        color: 'bg-green-500/20 text-green-300' },
  login_failed:   { label: 'Login failed', color: 'bg-red-500/20 text-red-300' },
  register:       { label: 'Register',     color: 'bg-indigo-500/20 text-indigo-300' },
  logout:         { label: 'Logout',       color: 'bg-slate-500/20 text-slate-300' },
  api_write:      { label: 'API write',    color: 'bg-blue-500/20 text-blue-300' },
};

const METHOD_COLOR = {
  POST:   'text-emerald-400',
  PUT:    'text-blue-400',
  PATCH:  'text-amber-400',
  DELETE: 'text-red-400',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ActivityLogTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [emailFilter, setEmailFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async (reset = true) => {
    setLoading(true);
    try {
      const params = { skip: reset ? 0 : skip, limit: LIMIT };
      if (emailFilter) params.email = emailFilter;
      if (actionFilter) params.action = actionFilter;
      if (pathFilter) params.path_contains = pathFilter;
      if (ipFilter) params.ip = ipFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await axios.get(`${API}/api/admin/activity-logs`, { params });
      setTotal(res.data.total || 0);
      if (reset) {
        setItems(res.data.items || []);
        setSkip(res.data.items?.length || 0);
      } else {
        setItems((prev) => [...prev, ...(res.data.items || [])]);
        setSkip((prev) => prev + (res.data.items?.length || 0));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [emailFilter, actionFilter, pathFilter, ipFilter, fromDate, toDate, skip]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/activity-logs/summary`, { params: { days: 7 } });
      setSummary(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    load(true);
    loadSummary();
    // eslint-disable-next-line
  }, []);

  const handleApplyFilters = () => load(true);
  const handleClearFilters = () => {
    setEmailFilter(''); setActionFilter(''); setPathFilter(''); setIpFilter(''); setFromDate(''); setToDate('');
    setTimeout(() => load(true), 0);
  };
  const handlePurge = async () => {
    if (!window.confirm('Delete activity logs older than 90 days? This cannot be undone.')) return;
    try {
      const res = await axios.delete(`${API}/api/admin/activity-logs/purge`, { params: { older_than_days: 90 } });
      toast.success(`Deleted ${res.data.deleted} old logs`);
      load(true);
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="activity-log-tab">
      {/* Header + Summary */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ClockCounterClockwise weight="fill" className="w-5 h-5 text-indigo-400" />
            Activity Log
          </h2>
          <p className="text-sm text-slate-500">
            Audit trail for logins, registrations, and write operations. Retained for compliance.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { load(true); loadSummary(); }}
            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm flex items-center gap-2"
            data-testid="activity-refresh-btn"
          >
            <ArrowClockwise className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handlePurge}
            className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm flex items-center gap-2"
            data-testid="activity-purge-btn"
          >
            <Trash className="w-4 h-4" />
            Purge {'>'}90d
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="glass-light rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Events (last {summary.window_days}d)</p>
            <p className="text-2xl font-bold text-white" data-testid="activity-stat-total">{summary.total_events?.toLocaleString?.() ?? summary.total_events}</p>
          </div>
          <div className="glass-light rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
              <Warning className="w-3 h-3 text-red-400" /> Failed logins
            </p>
            <p className="text-2xl font-bold text-red-300" data-testid="activity-stat-failed">{summary.failed_logins}</p>
          </div>
          <div className="glass-light rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Top user</p>
            <p className="text-sm text-white truncate">
              {summary.top_users?.[0]?.user_email || '—'}
            </p>
            <p className="text-xs text-slate-500">{summary.top_users?.[0]?.count || 0} events</p>
          </div>
          <div className="glass-light rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Top IP</p>
            <p className="text-sm text-white font-mono truncate">{summary.top_ips?.[0]?.ip || '—'}</p>
            <p className="text-xs text-slate-500">{summary.top_ips?.[0]?.count || 0} events</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="glass-light rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="relative md:col-span-2">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Email contains…"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              className="w-full bg-slate-900 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-500"
              data-testid="activity-filter-email"
            />
          </div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="activity-filter-action"
          >
            <option value="">All actions</option>
            <option value="login_success">Login</option>
            <option value="login_failed">Login failed</option>
            <option value="register">Register</option>
            <option value="logout">Logout</option>
            <option value="api_write">API write</option>
          </select>
          <input
            type="text"
            placeholder="Path contains…"
            value={pathFilter}
            onChange={(e) => setPathFilter(e.target.value)}
            className="bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500"
            data-testid="activity-filter-path"
          />
          <input
            type="text"
            placeholder="IP address"
            value={ipFilter}
            onChange={(e) => setIpFilter(e.target.value)}
            className="bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 font-mono"
            data-testid="activity-filter-ip"
          />
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-1/2 bg-slate-900 border border-white/10 rounded-lg px-2 py-2 text-sm text-white"
              data-testid="activity-filter-from"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-1/2 bg-slate-900 border border-white/10 rounded-lg px-2 py-2 text-sm text-white"
              data-testid="activity-filter-to"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={handleClearFilters}
            className="px-3 py-1.5 text-sm text-slate-400 hover:text-white"
            data-testid="activity-clear-filters"
          >
            Clear
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-sm font-semibold"
            data-testid="activity-apply-filters"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="glass-light rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between">
          <p className="text-sm text-slate-400">
            {loading ? 'Loading…' : `${items.length} of ${total.toLocaleString()} events`}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/70 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 whitespace-nowrap">Time</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">User</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Method</th>
                <th className="text-left px-3 py-2">Path</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((it) => {
                const meta = ACTION_META[it.action] || { label: it.action || '—', color: 'bg-slate-500/20 text-slate-300' };
                return (
                  <tr key={it.id} className="hover:bg-white/5" data-testid={`activity-row-${it.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-400 font-mono text-xs">{fmt(it.timestamp)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-white text-sm">{it.user_display_name || '—'}</div>
                      <div className="text-slate-500 text-xs">{it.user_email || 'anonymous'}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${meta.color}`}>{meta.label}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`font-mono text-xs font-semibold ${METHOD_COLOR[it.method] || 'text-slate-400'}`}>{it.method || '—'}</span>
                    </td>
                    <td className="px-3 py-2 max-w-md">
                      <span className="font-mono text-xs text-slate-300 truncate block">{it.path || '—'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`font-mono text-xs ${(it.status_code >= 400) ? 'text-red-400' : 'text-slate-400'}`}>{it.status_code ?? '—'}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{it.ip || '—'}</td>
                  </tr>
                );
              })}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center px-3 py-8 text-slate-500">No activity matches the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {items.length < total && (
          <div className="px-4 py-3 border-t border-white/10 flex justify-center">
            <button
              onClick={() => load(false)}
              disabled={loading}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm disabled:opacity-50"
              data-testid="activity-load-more"
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogTab;
